import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  arrangementCodes,
  showArrivalHeads,
  showMakeUp,
  type CleaningRow,
} from '@lemontaigu/staff';
import { useBoard } from '@lemontaigu/staff/react';
import { longDayLabel } from './ServiceLists.js';

// « Ménage » — the étage's sheet, a half-page of A4 landscape.
//
// Modelled on the one the operator types by hand today (photograph, 2026-09-09):
// three blocks, rooms ordered by number, a new line per floor, counts in
// parentheses. It is not a table — it is a dense list, because the whole point
// is that a floor fits on one line and the sheet fits on half a page.
//
// ── The three blocks are three different jobs ─────────────────────────────
//
//   A Blancs   the guest LEFT. The room is stripped and made from nothing.
//   Recouches  the guest is STILL HERE. Refreshed, not stripped.
//   Arrivées   someone COMES. The room must be ready and made up as asked.
//
// ── The underline ────────────────────────────────────────────────────────
//
// A room in BOTH « A Blancs » and « Arrivées » was vacated this morning and is
// re-let this afternoon: the tightest job of the day. It is underlined in BOTH
// blocks (0150) and its A Blancs line carries the ARRIVING party's count — the
// morning block is the one read when the trolley is loaded, so the job belongs
// there in full rather than split across two blocks to be joined by eye.
//
// ── The make-up, and what is honestly missing ────────────────────────────
//
// The operator's sheet writes `102(1,double, BB)` — one person, made up double,
// with a cot. Until 0157 the blank rule under those eleven convertible rooms
// was all this sheet could offer, because the configuration lives in Vega's
// reservation comment (`GCOMMENT`), which this platform does not mirror.
//
// 0157 replaces the blank with the CHOICE, printed: `209 [2S - D]`, the two
// arrangements the room can be in, coded D / 1S / 2S / 3S (operator,
// 2026-09-12). The one the arrival needs is circled and bold; when it is not
// known — which is everywhere today, since nothing writes
// `requested_bed_config` yet — both print plainly and the étage circles one in
// pen. That is the same amount of writing as the old rule and strictly more
// information, because the sheet now states what the two options ARE.
//
// The one the room is STANDING in prints first, once `current_bed_config` is
// fed by the interactive board. Until then the room's nominal arrangement leads.
//
// Only where the room has a choice, and only on the two lines where somebody
// acts on it: ARRIVÉES, and the A Blancs line of a room that turns over —
// *« Repeat same info in a blancs as when they too arrive »* (operator,
// 2026-09-12). A room vacated at 09:00 and re-let at 15:00 appears in both
// blocks, and the morning one is the block read with the trolley in the
// corridor: it has to state the whole job, exactly as 0150 already decided for
// the head count. A recouche is refreshed rather than remade, and a fixed room
// has nothing to decide, so neither carries it.
//
// Still missing: the cot. It has no column anywhere, and the blank space under
// the room number — the cell the print layout sizes to the day — is where it
// gets written.

const SECTIONS = [
  { key: 'ablancs', title: 'A Blancs' },
  { key: 'recouches', title: 'Recouches' },
  { key: 'arrivees', title: 'Arrivées' },
] as const;

/**
 * Rooms to a line.
 *
 * The operator asked for « 6 rooms per row », then clarified it was a rule of
 * thumb rather than a specification — so it is FIVE, chosen against the actual
 * constraint. The sheet is one vertical half of an A4 (132 mm of usable width)
 * and WIDTH is what binds: a busy day is ~16 room lines, about 95 mm of the
 * 190 mm of height available. Five columns give each cell ~26 mm against a
 * widest cell of ~19 mm — room for the pen stroke that crosses a finished room
 * off, which is what this sheet is for. Six gave ~21 mm and no slack at all.
 */
export const PER_ROW = 5;

/** Rooms of one section, grouped by floor and ordered within it. */
export function byFloor(rows: CleaningRow[]): { floor: number; rooms: CleaningRow[] }[] {
  const floors = new Map<number, CleaningRow[]>();
  for (const r of rows) {
    const list = floors.get(r.floor);
    if (list) list.push(r);
    else floors.set(r.floor, [r]);
  }
  return [...floors.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([floor, list]) => ({
      floor,
      // Numeric, not lexical: '99' must not sort after '101'. The rooms are
      // three digits today, so the two agree — which is exactly why a lexical
      // sort would survive review and break on the day a room is renumbered.
      rooms: [...list].sort((a, b) => Number(a.room) - Number(b.room)),
    }));
}

/**
 * A floor's rooms, cut into lines of `PER_ROW`.
 *
 * A floor still STARTS a new line — that is what makes the sheet scannable by
 * floor — but a floor of fourteen rooms wraps into lines of its own instead of
 * one long one. Chunked here rather than wrapped by CSS because the break must
 * be deterministic: the same day has to print identically on the desk's printer
 * and the office's, and a flow-wrapped line is at the mercy of whatever font
 * the driver substitutes.
 */
export function inRowsOfFive(rooms: CleaningRow[], per = PER_ROW): CleaningRow[][] {
  const out: CleaningRow[][] = [];
  for (let i = 0; i < rooms.length; i += per) out.push(rooms.slice(i, i + per));
  return out;
}

/**
 * How many grid lines the sheet will have — the number the page's height is
 * divided by.
 *
 * An EMPTY section still costs one line: it prints a « — », and a layout that
 * forgot it would size the cells slightly too tall and push the last line off
 * the page on exactly the days that have an empty block.
 */
export function sheetLineCount(rows: CleaningRow[], perRow = PER_ROW): number {
  let lines = 0;
  for (const { key } of SECTIONS) {
    const floors = byFloor(rows.filter((r) => r.section === key));
    if (floors.length === 0) {
      lines += 1;
      continue;
    }
    for (const f of floors) lines += Math.ceil(f.rooms.length / perRow);
  }
  return lines;
}

/**
 * ── The cell height is the DAY's, not the worst day's ──────────────────────
 *
 * This is the correction to two failed attempts (operator, 2026-09-14: « not
 * much of a difference », then « not good either way »). Both tried to pick one
 * static cell height that the worst day could survive — 42 à blancs + 42
 * arrivées, 21 lines — and then every ordinary day inherited the worst day's
 * cramped cell while leaving 60 % of the paper blank. Raising the font inside
 * that fixed cell only made it worse: at 14pt in a 7mm cell the number fills
 * the box, so the sheet gained type size by spending the very writing space the
 * étage had asked for.
 *
 * The sheet knows its own line count before it prints. So the height is
 * DIVIDED among the lines that actually exist: a 10-line day gets ~15mm cells
 * and fills the page, the worst day still gets its ~7mm and still fits. Nobody
 * pays August's price in February.
 *
 * `SHEET_BODY_MM` is measured, not derived — and re-measured on 2026-09-15
 * against the REAL font, which is what the first measurement got wrong. It was
 * taken in a mock-up set in Georgia while the app is set in Inter; Inter is
 * wider, the legend wrapped to three lines instead of two, and a sheet the
 * mock-up called « fits » printed on two pages. Re-measured with the actual
 * Google-Fonts Inter, the operator's own 15-Sept day came to 195.7mm against a
 * 190mm box — reproducing the failure exactly, which is the only reason to
 * trust the number that replaced it.
 *
 * With the legend moved to the right column (see the render below), the left
 * column's overhead is just the title, three headings and block margins:
 * ~28mm measured at the 21-line worst day. 190 - 28 = 162mm available, so 152
 * leaves ~11mm of real slack for a driver that scales slightly.
 *
 * **If this needs re-deriving, measure it in a browser with Inter actually
 * loaded** (`document.fonts.check('14pt Inter')`), never from font arithmetic
 * and never in a substituted face.
 *
 * `CELL_MAX_MM` is the one judgement call here: ~18mm is two written lines of
 * pen. A quiet day could mathematically afford 50mm cells, which would be a
 * sheet of six rooms floating in white. If the étage wants more, this is the
 * line to change.
 */
export const SHEET_BODY_MM = 152;
export const CELL_MIN_MM = 7;
export const CELL_MAX_MM = 18;

export function cellHeightMm(lines: number): number {
  if (lines <= 0) return CELL_MAX_MM;
  const fit = SHEET_BODY_MM / lines;
  const clamped = Math.max(CELL_MIN_MM, Math.min(CELL_MAX_MM, fit));
  // A tenth of a millimetre, rounded DOWN: rounding up is how 21 lines each
  // gain 0.05mm and the sheet quietly grows past the page box.
  return Math.floor(clamped * 10) / 10;
}

function RoomCell({ row }: { row: CleaningRow }) {
  // A room is made up for TWO unless told otherwise (operator, 2026-09-12), so
  // the count prints only when it changes the work. Forty « (2) » on a morning
  // sheet are forty things to read past to find the « (1) » that matters. This
  // now applies uniformly to recouches too (operator, 2026-09-14).
  const heads = showArrivalHeads(row.section, row.guests) ? row.guests : null;

  // The make-up belongs to the arrival — and to the A Blancs line of a room
  // that turns over, which is the SAME job read three hours earlier, with the
  // trolley in the corridor. See `showMakeUp`.
  const codes = showMakeUp(row) ? arrangementCodes(row) : null;

  // An arrival that is NOT a turnover: nobody left that room today, so it was
  // already vacant and cleaned. Operator, 2026-09-10 — the job there is to
  // check it really is clean and to lay out soap and towels for the right
  // number of people, and *« it's also safe to go there because its occupants
  // are necessarily gone already »*. Bold says: start here, it is quick and it
  // cannot be occupied.
  const ready = row.section === 'arrivees' && !row.turnover;

  return (
    <span className={`clean-room${ready ? ' is-ready' : ''}`}>
      <span className={row.turnover ? 'is-relet' : undefined}>{row.room}</span>
      {heads != null && <span className="clean-heads">({heads})</span>}
      {codes && (
        <span className="clean-config" title={row.arrangements.map((a) => a.code).join(" / ") || undefined}>
          [
          {codes.map((c, i) => (
            <span key={c.which}>
              {i > 0 && <span className="clean-config-sep"> - </span>}
              <span className={c.circled ? 'is-chosen' : undefined}>{c.code}</span>
            </span>
          ))}
          ]
        </span>
      )}
    </span>
  );
}

/** The sheet's rows for a date. Split out so the printable sheet and the
 *  phone tab load them the same way and cannot drift. */
export function useCleaningRows(day: string): {
  rows: CleaningRow[] | null;
  err: string;
} {
  const board = useBoard();
  const [rows, setRows] = useState<CleaningRow[] | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setErr('');
    void (async () => {
      try {
        const r = await board.cleaningList(day);
        if (!cancelled) setRows(r);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [board, day]);

  return { rows, err };
}

/**
 * The three blocks. `perRow` is the only difference between the printed sheet
 * (five, sized to one vertical half of an A4) and the phone (three, sized to a
 * hand) — the rows, the marks and the counts are identical, because a staff
 * member and the étage reading two different sheets is the failure this
 * replaces, not one it may introduce.
 *
 * ── A row is a ROW, not five aligned columns (2026-09-15) ─────────────
 *
 * Until now every room was a cell in one `repeat(5, max-content)` grid per
 * block, so a column was as wide as the widest cell ANYWHERE in that block. One
 * `[D - 2S]` in column four widened column four on every line, including the
 * thirty-one rooms that have no choice to print — operator, 2026-09-15:
 * *« the rooms without no alt config do not need so much space »*.
 *
 * Worse, the block's width was the SUM of those five maxima, which is ≥ any
 * real line's width and could exceed the 129mm half: *« the last room on the
 * row bleeds to the right »*. It was bleeding into the right-hand column.
 *
 * So each line is its own flex row: chunks sized to their own content, one
 * uniform space between them. Nothing aligns vertically any more, and nothing
 * needs to — the sheet is read along a line, not down a column.
 *
 * Measured in Inter at 14pt, one chunk against the 129mm half-page:
 *
 *   `104`                    9.6mm      a line of five plain   68mm
 *   `104 (1)`               17.9mm
 *   `104 [D - 2S]`          24.4mm      five of these         142mm  ✗
 *   `104 (1) [D - 2S]`      32.7mm      five of these         183mm  ✗
 *   `104 (1) [D+1S - 3S]`   39.3mm
 *
 * **Two full-width chunks per line of five is the ceiling** (2 wide + 3 plain =
 * 114mm; three wide = 137mm). Real sheets sit well inside it — only eleven of
 * the forty-two rooms have a choice to print at all, and the widest line of the
 * operator's 15-Sept day measured 110.7mm — but a line that did exceed it
 * WRAPS (`flex-wrap` on `.clean-row`) instead of bleeding into the right-hand
 * column. That is the guard, not the design: a wrapped line is one more line
 * than `sheetLineCount` predicted, so it eats into the ~11mm of height slack.
 * One is absorbed; if two ever appear on a real sheet, `PER_ROW` is the thing
 * to lower, not the gap.
 */
export function CleaningBlocks({
  rows,
  perRow,
}: {
  rows: CleaningRow[];
  perRow: number;
}) {
  const bySection = useMemo(() => {
    const map = new Map<string, CleaningRow[]>();
    for (const r of rows) {
      const list = map.get(r.section);
      if (list) list.push(r);
      else map.set(r.section, [r]);
    }
    return map;
  }, [rows]);

  return (
    <>
      {SECTIONS.map(({ key, title }) => {
        const of = bySection.get(key) ?? [];
        const floors = byFloor(of);
        return (
          <section key={key} className="clean-block">
            {/* The room COUNT beside the block name. « Combien de blancs
                demain ?! » is how the day is asked about (operator,
                2026-09-10), so the answer belongs in the heading rather than
                at the end of a line someone has to count. */}
            <h3>
              {title}
              <span className="clean-count">{of.length}</span>
            </h3>
            {floors.length === 0 ? (
              <p className="clean-line clean-empty">—</p>
            ) : (
              <div className="clean-grid">
                {floors.flatMap(({ floor, rooms }) =>
                  inRowsOfFive(rooms, perRow).map((line, i) => (
                    <div className="clean-row" key={`${key}-${floor}-${i}`}>
                      {line.map((r) => (
                        <RoomCell key={`${r.section}-${r.room}`} row={r} />
                      ))}
                    </div>
                  )),
                )}
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}

/**
 * Where to start: the already-free arrivals, densest floor first.
 *
 * Operator, 2026-09-10 — a first insight, with more expected from use. The
 * reasoning is theirs and it is worth keeping next to the code, because the
 * order is otherwise a black box on a printed page:
 *
 *   * an arrival whose room nobody vacated today is **already clean**, so the
 *     job is to check it and lay out soap and towels for the right number;
 *   * it is **safe to enter at any hour** — its previous occupant is
 *     necessarily gone;
 *   * so it is the quickest way to turn rooms from « to do » into « done »,
 *     early, before the morning's departures have even left.
 *
 * Densest floor first because the trolley moves once and clears the most: the
 * cost on an étage round is the stairs, not the doors.
 *
 * Ties break on the LOWER floor. Arbitrary, and stated rather than silent — if
 * the étage would rather start at the top and work down, this is the one line
 * to change.
 */
export function startOrder(rows: CleaningRow[]): { floor: number; rooms: CleaningRow[] }[] {
  const ready = rows.filter((r) => r.section === 'arrivees' && !r.turnover);
  return byFloor(ready).sort(
    (a, b) => b.rooms.length - a.rooms.length || a.floor - b.floor,
  );
}

export function CleaningPlan({ rows }: { rows: CleaningRow[] }) {
  const order = startOrder(rows);
  if (order.length === 0) return null;

  return (
    <aside className="clean-plan">
      <h3>Par où commencer</h3>
      <p className="clean-plan-why">
        Chambres déjà libres : rapides à faire (vérifier, garnir) et sûres —
        leurs occupants sont partis. L’étage qui en compte le plus d’abord.
      </p>
      <ol className="clean-plan-list">
        {order.map(({ floor, rooms }) => (
          <li key={floor}>
            <span className="clean-plan-floor">
              {floor}<sup>e</sup> étage
              <span className="clean-count">{rooms.length}</span>
            </span>
            <span className="clean-plan-rooms">
              {rooms.map((r) => r.room).join('  ')}
            </span>
          </li>
        ))}
      </ol>
    </aside>
  );
}

/** What the three marks mean. Worth its line: without it the typography is
 *  folklore taught to each new member of the étage, and taught wrong once is
 *  worse than absent. */
export function CleaningLegend() {
  return (
    <p className="clean-legend">
      <span className="is-relet">souligné</span> = départ puis arrivée le même
      jour · <strong>gras</strong> = chambre déjà libre, à vérifier et à garnir ·
      (n) = nombre de personnes si différent de 2 · [ … ] = configurations
      possibles (D = lit 2 places, 1S/2S/3S = lits simples), entourer celle à
      faire · <span className="is-chosen">entourée</span> = configuration
      demandée
    </p>
  );
}

export function CleaningList({ day, onClose }: { day: string; onClose: () => void }) {
  const { rows, err } = useCleaningRows(day);

  // `@page` is document-level, so the orientation cannot be scoped to this
  // component by CSS alone — the class on <html> is what selects the landscape
  // page box, and it must come off when the sheet closes or the NEXT print
  // (the effectifs sheet, which is portrait) comes out sideways.
  useEffect(() => {
    document.documentElement.classList.add('printing-landscape');
    return () => document.documentElement.classList.remove('printing-landscape');
  }, []);

  // The day's own cell height, handed to the print CSS as a variable. Computed
  // here rather than in CSS because the line count is a property of the DATA —
  // floors, and how they chunk into fives — which no CSS expression can see.
  const cell = rows ? cellHeightMm(sheetLineCount(rows, PER_ROW)) : CELL_MIN_MM;

  return (
    <div
      className="cleaning-list"
      style={{ '--clean-cell': `${cell}mm` } as CSSProperties}
    >
      <div className="service-actions">
        <button type="button" onClick={() => window.print()} disabled={!rows}>
          Imprimer
        </button>
        <button type="button" onClick={onClose}>
          Fermer
        </button>
      </div>

      <h2 className="service-title">Ménage — {longDayLabel(day)}</h2>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!err && rows === null && <p className="staff-empty">Chargement…</p>}
      {rows && (
        /* Two columns on paper, one per HALF of the A4: the list on the left,
           everything that is READ RATHER THAN WRITTEN ON on the right. The
           étage folds the sheet down the middle and carries it, so the right
           half is the back — a place a suggestion and a key can live without
           competing with the list for the eye.

           The legend moved here on 2026-09-15 (operator: « the explanations at
           the end of the page should be moved to the right, so we have only one
           page »). It was the last thing in the LEFT column, so its three
           wrapped lines were what pushed a full day onto a second sheet — while
           the right half sat three-quarters empty. It is a key, consulted rarely
           and never written on, so the back of the fold is where it belongs. */
        <div className="cleaning-sheet">
          <div className="cleaning-sheet-list">
            <CleaningBlocks rows={rows} perRow={PER_ROW} />
          </div>
          <div className="cleaning-sheet-aside">
            <CleaningPlan rows={rows} />
            <CleaningLegend />
          </div>
        </div>
      )}
    </div>
  );
}
